import { useEffect, useRef } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import JsBarcode from "jsbarcode";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, Loader2, Package } from "lucide-react";
import { useApi, useAdminSession, SITE, fmtDay } from "@/lib/api";

const TRACK_BASE = `https://${SITE.domain}/track/`;

function Barcode({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) JsBarcode(ref.current, value, { format: "CODE128", width: 2.2, height: 70, displayValue: true, font: "monospace", fontSize: 16, margin: 0 });
  }, [value]);
  return <svg ref={ref} data-testid="label-barcode" className="max-w-full" />;
}

const Party = ({ title, p, big }) => (
  <div className="p-4 border-b-2 border-black">
    <p className="text-[10px] font-bold uppercase tracking-widest">{title}</p>
    <p className={`font-bold ${big ? "text-xl" : "text-base"}`}>{p.name}</p>
    <p className={big ? "text-base" : "text-sm"}>{p.address}</p>
    {p.phone && <p className="text-sm">Tel: {p.phone}</p>}
  </div>
);

function Label({ s }) {
  const p = s.package;
  return (
    <div className="label-sheet bg-white text-black border-2 border-black w-full max-w-[4in] mx-auto font-sans" data-testid="shipping-label">
      <div className="flex items-center justify-between p-4 border-b-2 border-black bg-black text-white print-keep-bg">
        <span className="flex items-center gap-2 font-display font-extrabold tracking-tight"><Package className="h-5 w-5" /> INLA SHIPPING HUB</span>
        <span className="text-xs font-bold uppercase">{p.service}</span>
      </div>
      <Party title="From" p={s.sender} />
      <Party title="Ship to" p={s.receiver} big />
      <div className="grid grid-cols-3 text-center border-b-2 border-black text-sm">
        <div className="p-2 border-r-2 border-black"><p className="text-[10px] font-bold uppercase">Weight</p><p className="font-bold">{p.weight_kg} kg</p></div>
        <div className="p-2 border-r-2 border-black"><p className="text-[10px] font-bold uppercase">Pieces</p><p className="font-bold">{p.quantity}</p></div>
        <div className="p-2"><p className="text-[10px] font-bold uppercase">Ship date</p><p className="font-bold">{fmtDay(s.created_at)}</p></div>
      </div>
      <div className="p-4 border-b-2 border-black flex justify-center"><Barcode value={s.tracking_number} /></div>
      <div className="p-4 flex items-center gap-4">
        <QRCodeSVG value={TRACK_BASE + s.tracking_number} size={92} data-testid="label-qr" />
        <div className="text-xs space-y-1 min-w-0">
          <p className="font-bold uppercase">{s.origin} → {s.destination}</p>
          <p>Contents: {p.contents}</p>
          {p.dimensions && <p>Dims: {p.dimensions}</p>}
          <p className="break-all">Track: {SITE.domain}</p>
        </div>
      </div>
    </div>
  );
}

export default function LabelPage() {
  const { id } = useParams();
  const api = useApi();
  const { data: session, isLoading: meLoading, isError } = useAdminSession();
  const q = useQuery({ queryKey: ["label", id], queryFn: () => api("get", `/admin/shipments/${id}`), enabled: !!session, retry: false });
  if (isError) return <Navigate to="/admin/login" replace />;
  if (meLoading || !session) return <div className="p-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <style>{"@media print { @page { size: 4in 6in; margin: 0.15in; } }"}</style>
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0A192F]" data-testid="label-back-link"><ArrowLeft className="h-4 w-4" /> Back to admin</Link>
        <button onClick={() => window.print()} disabled={!q.data} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF6B00] hover:bg-[#E05D00] text-white font-semibold text-sm rounded-sm disabled:opacity-50" data-testid="label-print-btn"><Printer className="h-4 w-4" /> Print label</button>
      </div>
      {q.isLoading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
      {q.isError && <p className="text-slate-500" data-testid="label-not-found">Shipment not found.</p>}
      {q.data && <Label s={q.data} />}
    </div>
  );
}
